import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { apiRequest, clearStoredToken, getStoredToken, storeToken } from "./api";
import type { AuthResponse, User } from "./types";

type AuthContextValue = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName?: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function loadUser() {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const me = await apiRequest<User>("/api/auth/me");
        if (alive) setUser(me);
      } catch {
        clearStoredToken();
        if (alive) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    loadUser();
    return () => {
      alive = false;
    };
  }, [token]);

  async function login(email: string, password: string) {
    const response = await apiRequest<AuthResponse>("/api/auth/login", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ email, password }),
    });
    storeToken(response.token);
    setToken(response.token);
    setUser(response.user);
  }

  async function register(email: string, password: string, fullName?: string) {
    const response = await apiRequest<AuthResponse>("/api/auth/register", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ email, password, full_name: fullName || null }),
    });
    storeToken(response.token);
    setToken(response.token);
    setUser(response.user);
  }

  function logout() {
    clearStoredToken();
    setToken(null);
    setUser(null);
  }

  const value = useMemo(
    () => ({ user, token, loading, login, register, logout }),
    [user, token, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
